import { useTranslation } from 'react-i18next';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { ArrowRightLeft, MapPin } from 'lucide-react';
import { useFuelTransaction } from '@/hooks/useFuel';

export function FuelTransactionDetailSheet({
  id,
  onClose,
}: {
  id: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: tx, isLoading } = useFuelTransaction(id);

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('fuel.detail.title')}</SheetTitle>
        </SheetHeader>
        {isLoading || !tx ? (
          <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Big amount */}
            <div className="rounded-md border border-border p-4 bg-muted/20">
              <div className="text-xs text-muted-foreground uppercase">{t('fuel.detail.amount')}</div>
              <div className="text-3xl font-semibold mt-1">
                {Number(tx.total_sar).toLocaleString()} <span className="text-sm font-normal text-muted-foreground">SAR</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {Number(tx.liters).toFixed(2)} L
                {tx.price_per_liter_sar ? <> · {Number(tx.price_per_liter_sar).toFixed(2)} SAR/L</> : null}
                {tx.vat_sar ? <> · VAT {Number(tx.vat_sar).toFixed(2)}</> : null}
              </div>
            </div>

            {/* Card movement notice */}
            {tx.card_movements?.length ? (
              <div className="rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-500/10 p-3">
                <div className="flex items-start gap-2 text-sm">
                  <ArrowRightLeft className="h-4 w-4 mt-0.5 text-amber-600" />
                  <div>
                    <div className="font-medium text-amber-800 dark:text-amber-300">
                      {t('fuel.detail.movementNotice')}
                    </div>
                    <div className="text-xs text-amber-700 dark:text-amber-200/80 mt-1">
                      {tx.card_movements[0].movement_type === 'emergency_swap' ? (
                        <>
                          {t('fuel.detail.movementSwap')} →{' '}
                          <strong>{tx.card_movements[0].actual_vehicle_plate}</strong>
                        </>
                      ) : (
                        <>{t(`fuel.movementType.${tx.card_movements[0].movement_type}`)}</>
                      )}
                      {tx.card_movements[0].authorization_reason ? (
                        <div className="mt-1 italic">"{tx.card_movements[0].authorization_reason}"</div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label={t('fuel.detail.date')} value={new Date(tx.transaction_at).toLocaleString()} />
              <Field label={t('fuel.detail.provider')} value={tx.provider} />
              <Field label={t('fuel.detail.vehicle')} value={tx.vehicle_plate ?? tx.vehicle_code ?? '—'} />
              <Field
                label={t('fuel.detail.attributedVehicle')}
                value={
                  tx.cost_attributed_vehicle_id !== tx.vehicle_id
                    ? (tx.attributed_vehicle_plate ?? tx.attributed_vehicle_code ?? '—')
                    : t('fuel.detail.sameAsVehicle')
                }
              />
              <Field label={t('fuel.detail.driver')} value={tx.driver_name ?? tx.provider_driver_name ?? '—'} />
              <Field label={t('fuel.detail.cardNumber')} value={tx.card_number ?? '—'} />
              <Field label={t('fuel.detail.fuelType')} value={tx.fuel_type ?? '—'} />
              <Field label={t('fuel.detail.payment')} value={tx.payment_method ?? '—'} />
              <Field label={t('fuel.detail.odometer')} value={tx.odometer_km ? `${tx.odometer_km.toLocaleString()} km` : '—'} />
              <Field
                label="L/100km"
                value={tx.liters_per_100km != null ? Number(tx.liters_per_100km).toFixed(2) : '—'}
              />
              <Field
                label={t('fuel.detail.station')}
                value={tx.station_name ?? '—'}
              />
              <Field label={t('fuel.detail.stationDir')} value={
                tx.city || tx.district ? `${tx.city ?? ''}${tx.city && tx.district ? ' / ' : ''}${tx.district ?? ''}` : '—'
              } />
            </div>

            {/* Map link */}
            {tx.station_lat && tx.station_lng ? (
              <a
                href={`https://www.google.com/maps?q=${tx.station_lat},${tx.station_lng}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                <MapPin className="h-4 w-4" />
                {t('fuel.detail.viewOnMap')}
              </a>
            ) : null}

            {tx.is_anomaly ? (
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm dark:bg-red-500/10 dark:border-red-500/40">
                <Badge className="bg-red-500/20 text-red-700 border-red-500/30">{t('fuel.detail.anomaly')}</Badge>
                {tx.anomaly_type ? <span className="ml-2 text-xs">{tx.anomaly_type}</span> : null}
              </div>
            ) : null}

            {/* External ID for debugging */}
            <div className="text-xs text-muted-foreground border-t border-border pt-3 mt-2">
              {t('fuel.detail.externalId')}: {tx.external_id}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase">{label}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}
